import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Dropdown } from "primereact/dropdown";
import { useEffect, useRef, useState } from "react";
import { useAddVisitNote, useUpdateVisitNote } from "../../hooks/useGetDataQuery";
import { useToastContext } from "../../Lib/Contexts/toastContext";
import { FloatLabel } from "primereact/floatlabel";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { InputNumber } from "primereact/inputnumber";
import { Chip } from "primereact/chip";
import { set } from "date-fns";
import { useTranslation } from "react-i18next";

const VisitInformation = ({ selectedPatient, visitNoteList, setVisitNoteList, diagnosisOptions, t }: any) => {

    const {
        mutate: addVisitNoteMutation,
        data: addVisitNoteMutationData,
        isSuccess: isSuccessAddVisitNoteMutation,
        isError: isErrorAddVisitNoteMutation,
        error: addVisitNoteMutationError
    } = useAddVisitNote();

    const {
        mutate: updateVisitNoteMutation,
        isSuccess: isSuccessUpdateVisitNoteMutation,
        isError: isErrorUpdateVisitNoteMutation,
        error: updateVisitNoteMutationError
    } = useUpdateVisitNote();


    const lang = localStorage.getItem("i18nextLng") === "en" ? "en-US" : "tr-TR";
    const { showToast } = useToastContext();

    const [selectedDate, setSelectedDate] = useState<any>()
    const [dateOptions, setDateOptions] = useState<any>([])

    const [reasonForComing, setReasonForComing] = useState<any>("");

    const [generalHealthCondition, setGeneralHealthCondition] = useState<any>("");
    const [drugsUsed, setDrugsUsed] = useState<any>("");
    const [previousDiseases, setPreviousDiseases] = useState<any>("");
    const [previousSurgicalOperations, setPreviousSurgicalOperations] = useState<any>("");
    const [familyHistoryOfDiseases, setFamilyHistoryOfDiseases] = useState<any>("");
    const [allergies, setAllergies] = useState<any>("");
    const [lifeStyle, setLifeStyle] = useState<any>("");

    const [diastolicBloodPressure, setDiastolicBloodPressure] = useState<any>(null);
    const [systolicBloodPressure, setSystolicBloodPressure] = useState<any>(null);
    const [pulse, setPulse] = useState<any>(null);
    const [saturationSpO2, setSaturationSpO2] = useState<any>(null);
    const [respiratoryRate, setRespiratoryRate] = useState<any>(null);
    const [bodyTemperature, setBodyTemperature] = useState<any>(null);

    const [jointRangeOfMotion, setJointRangeOfMotion] = useState<any>("");
    const [muscleStrength, setMuscleStrength] = useState<any>("");
    const [postureAnalysis, setPostureAnalysis] = useState<any>("");
    const [painAssessment, setPainAssessment] = useState<any>("");


    const [diagnosisCode, setDiagnosisCode] = useState<any>("");
    const [diagnosisList, setDiagnosisList] = useState<any>([]);

    const [today, setToday] = useState<any>(false);

    const [updatedVisitNote, setUpdatedVisitNote] = useState<any>();

    const [reasonForComingIsNotGiven, setReasonForComingIsNotGiven] = useState<any>(false);
    const [generalHealthConditionIsNotGiven, setGeneralHealthConditionIsNotGiven] = useState<any>(false);
    const [diagnosisCodeIsNotGiven, setDiagnosisCodeIsNotGiven] = useState<any>(false);

    const cardRef = useRef<any>(null);
    const [offset, setOffset] = useState({ right: 20, bottom: 20 });

    const { i18n } = useTranslation();


    useEffect(() => {
        const updatePosition = () => {
            if (cardRef.current) {
                const rect = cardRef.current.getBoundingClientRect();
                setOffset({
                    right: window.innerWidth - rect.right + 20,
                    bottom: window.innerHeight - rect.bottom + 20
                });
            }
        };

        window.addEventListener("resize", updatePosition);
        window.addEventListener("scroll", updatePosition);
        updatePosition();

        return () => {
            window.removeEventListener("resize", updatePosition);
            window.removeEventListener("scroll", updatePosition);
        };
    }, []);

    useEffect(() => {
        if (visitNoteList) {
            const formattedDates = visitNoteList.map((note: any) =>
                new Date(note.visitDate).toLocaleDateString(lang, {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric'
                })
            );

            setDateOptions(formattedDates);

            if (isToday(visitNoteList[0]?.visitDate)) {
                setSelectedDate(formattedDates[0]);
                setToday(true)
                setValues(false, visitNoteList[0]);
            }
        }
    }, [visitNoteList]);

    useEffect(() => {
        if (diagnosisCode) {
            setDiagnosisList((prevList: any) => {
                if (prevList.includes(diagnosisCode)) {
                    return prevList;
                }
                return [...prevList, diagnosisCode];
            });
            setDiagnosisCode("");
        }
    }, [diagnosisCode]);


    useEffect(() => {
        if (isSuccessAddVisitNoteMutation) {
            showToast({ severity: 'success', summary: t("success"), detail: t("visitNoteAdded"), life: 1000 });

            setVisitNoteList((prevList: any) => [addVisitNoteMutationData.data, ...prevList]);
        }
    }, [isSuccessAddVisitNoteMutation]);

    useEffect(() => {
        if (isErrorAddVisitNoteMutation) {
            showToast({ severity: 'error', summary: 'Error', detail: addVisitNoteMutationError.message, life: 1000 });
        }
    }, [isErrorAddVisitNoteMutation]);

    useEffect(() => {
        if (isSuccessUpdateVisitNoteMutation) {
            showToast({ severity: 'success', summary: t("success"), detail: t("visitNoteUpdated"), life: 1000 });

            setVisitNoteList((prevList: any) => [updatedVisitNote, ...prevList.slice(1)]);
        }
    }, [isSuccessUpdateVisitNoteMutation]);

    useEffect(() => {
        if (isErrorUpdateVisitNoteMutation) {
            showToast({ severity: 'error', summary: 'Error', detail: updateVisitNoteMutationError.message, life: 1000 });
        }
    }, [isErrorUpdateVisitNoteMutation]);

    useEffect(() => {
        if (visitNoteList) {
             setVisitNoteList((prevList: any) => { return [...prevList] })
        }
    }, [i18n.language]);


    const setValues = (def: any, visitNote: any) => {
        setReasonForComing(def ? "" : visitNote.reasonForComing);

        setGeneralHealthCondition(def ? "" : visitNote.anamnez?.generalHealthCondition);
        setDrugsUsed(def ? "" : visitNote.anamnez?.drugsUsed);
        setPreviousDiseases(def ? "" : visitNote.anamnez?.previousDiseases);
        setPreviousSurgicalOperations(def ? "" : visitNote.anamnez?.previousSurgicalOperations);
        setFamilyHistoryOfDiseases(def ? "" : visitNote.anamnez?.familyHistoryOfDiseases);
        setAllergies(def ? "" : visitNote.anamnez?.allergies);
        setLifeStyle(def ? "" : visitNote.anamnez?.lifeStyle);


        setDiastolicBloodPressure(def ? null : visitNote.vitalSigns?.bloodPressure?.diastolicBloodPressure);
        setSystolicBloodPressure(def ? null : visitNote.vitalSigns?.bloodPressure?.systolicBloodPressure);
        setPulse(def ? null : visitNote.vitalSigns?.pulse);
        setSaturationSpO2(def ? null : visitNote.vitalSigns?.saturationSpO2);
        setRespiratoryRate(def ? null : visitNote.vitalSigns?.respiratoryRate);
        setBodyTemperature(def ? null : visitNote.vitalSigns?.bodyTemperature);


        setJointRangeOfMotion(def ? "" : visitNote.physicalExamination?.jointRangeOfMotion);
        setMuscleStrength(def ? "" : visitNote.physicalExamination?.muscleStrength);
        setPostureAnalysis(def ? "" : visitNote.physicalExamination?.postureAnalysis);
        setPainAssessment(def ? "" : visitNote.physicalExamination?.painAssessment);

        setDiagnosisList(def ? [] : visitNote.diagnosisList);

    }

    const isToday = (dateStr: any) => {
        const inputDate = new Date(dateStr);
        const today = new Date();

        return (
            inputDate.getFullYear() === today.getFullYear() &&
            inputDate.getMonth() === today.getMonth() &&
            inputDate.getDate() === today.getDate()
        );
    };


    const selectVisitNote = (e: any) => {
        setSelectedDate(e.value)
        if (e.value) {

            if (isToday(e.value)) {
                setToday(true);
            }
            else {
                setToday(false);
            }

            const formatDate = (dateStr: string) =>
                new Date(dateStr).toLocaleDateString(lang, {
                    day: "2-digit",
                    month: "short",
                    year: "numeric"
                }).replace(/\u202F|\u00A0/g, ' ');

            const visitNote = visitNoteList.find((note: any) => formatDate(note.visitDate) === e.value);
            setValues(false, visitNote);
        }
        else {
            setValues(true, null);
            setToday(false);
        }
    }

    const addConsultantNote = () => {
        let abort = false;

        if (!generalHealthCondition) {
            setGeneralHealthConditionIsNotGiven(true);
            abort = true;
        }
        if (!reasonForComing) {
            setReasonForComingIsNotGiven(true);
            abort = true;
        }

        if (diagnosisList.length === 0) {
            setDiagnosisCodeIsNotGiven(true);
            abort = true;
        }

        if (abort) {
            showToast({ severity: 'error', summary: t("error"), detail: t("someMandatoryFieldsAreEmpty"), life: 1000 });
            return
        }


        const visitNote: any = {
            anamnez: {
                generalHealthCondition: generalHealthCondition,
                drugsUsed: drugsUsed,
                previousDiseases: previousDiseases,
                previousSurgicalOperations: previousSurgicalOperations,
                familyHistoryOfDiseases: familyHistoryOfDiseases,
                allergies: allergies,
                lifeStyle: lifeStyle
            },
            vitalSigns: {
                bloodPressure: {
                    diastolicBloodPressure: diastolicBloodPressure,
                    systolicBloodPressure: systolicBloodPressure
                },
                pulse: pulse,
                saturationSpO2: saturationSpO2,
                respiratoryRate: respiratoryRate,
                bodyTemperature: bodyTemperature
            },
            physicalExamination: {
                jointRangeOfMotion: jointRangeOfMotion,
                muscleStrength: muscleStrength,
                postureAnalysis: postureAnalysis,
                painAssessment: painAssessment
            },
            diagnosisList: diagnosisList,
            reasonForComing: reasonForComing,
            patientId: selectedPatient.id,

        }

        if (!today) {
            addVisitNoteMutation(visitNote);

        }
        else {
            visitNote['visitDate'] = new Date().toISOString();
            visitNote["visitId"] = visitNoteList[0].visitId;
            updateVisitNoteMutation(visitNote);
            setUpdatedVisitNote(visitNote);
        }
    }

    const floatinglabelForText = (valueData: any, setValueData: any, i18Data: any,) => {
        return (
            <FloatLabel>
                <InputText value={valueData} onChange={(e) => setValueData(e.target.value)}
                    disabled={selectedDate && !today}
                    style={{
                        height: '3rem',
                        width: '100%',
                        borderRadius: "15px",
                    }} />
                <label>{t(i18Data)}</label>
            </FloatLabel>
        )
    }

    const floatinglabelForTextArea = (valueData: any, setValueData: any, i18Data: any, invalid: any) => {
        return (
            <FloatLabel>
                <InputTextarea value={valueData} onChange={(e) => setValueData(e.target.value)}
                    disabled={selectedDate && !today}
                    rows={2}
                    invalid={invalid}
                    autoResize
                    style={{
                        width: '100%',
                        borderRadius: "15px",
                    }} />
                <label className="flex align-items-center gap-1">
                    {(i18Data === 'reasonForComing' || i18Data === 'generalHealthCondition')
                        && <i className="pi pi-asterisk p-overlay-badge" style={{ fontSize: '0.5rem', color: 'red' }}></i>}
                    {t(i18Data)}
                </label>
            </FloatLabel>
        )
    }

    const floatinglabelForNumber = (valueData: any, setValueData: any, i18Data: any, fraction: any, minValue: any, maxValue: any) => {
        return (
            <FloatLabel>
                <InputNumber
                    value={valueData}
                    onValueChange={(e) => setValueData(e.value)}
                    maxFractionDigits={fraction}
                    min={minValue}
                    max={maxValue}
                    disabled={selectedDate && !today}
                    style={{
                        height: '3rem',
                        width: '100%',
                        borderRadius: "15px",
                    }} />
                <label >{t(i18Data)}</label>
            </FloatLabel>
        )
    }

    return (
        <Card ref={cardRef} className="flex-grow-1" style={{ borderRadius: "15px", position: "relative", overflowY: "auto" }}>
            <div className="flex align-items-start justify-content-between">
                <p className="m-0 text-lg font-bold" style={{ color: "#2D3967" }}>{t('visitInfo')}</p>

                <div className="flex gap-4">
                    <Dropdown
                        value={selectedDate}
                        placeholder={t('selecPreviousNotes')}
                        onChange={(e) => selectVisitNote(e)}
                        showClear
                        options={dateOptions}
                        optionLabel="name"
                        style={{
                            height: '3rem',
                            width: '12rem',
                            borderRadius: "15px",
                        }}
                    />

                    <Button label={t('bringPrevAnamnez')}
                        onClick={() => {
                            setValues(visitNoteList.length !== 0 ? false : true,
                                visitNoteList.length !== 0 ? visitNoteList[0] : null);
                        }}
                        style={{
                            color: "#FFFFFF",
                            backgroundColor: "#2D3967",
                            height: '3rem',
                            width: '12rem',
                            borderRadius: "15px",
                            border: "none"
                        }} />
                </div>
            </div>

            <br />
            {floatinglabelForTextArea(reasonForComing, setReasonForComing, "reasonForComing", reasonForComingIsNotGiven && !reasonForComing)}
            <br />

            <div className="flex justify-content-between gap-4">
                <div className="flex flex-column gap-4 w-full" style={{ backgroundColor: "rgba(234, 235, 240, 0.3)", padding: "1rem", borderRadius: "15px" }}>
                    <p className="m-0 font-bold" style={{ color: "#2D3967", whiteSpace: "nowrap" }}>{t('anamnez')}</p>
                    {floatinglabelForTextArea(generalHealthCondition, setGeneralHealthCondition, "generalHealthCondition", generalHealthConditionIsNotGiven && !generalHealthCondition)}
                    {floatinglabelForTextArea(drugsUsed, setDrugsUsed, "drugsUsed", false)}
                    {floatinglabelForTextArea(previousDiseases, setPreviousDiseases, "previousDiseases", false)}
                    {floatinglabelForTextArea(previousSurgicalOperations, setPreviousSurgicalOperations, "previousSurgicalOperations", false)}
                    {floatinglabelForTextArea(familyHistoryOfDiseases, setFamilyHistoryOfDiseases, "familyHistoryOfDiseases", false)}
                    {floatinglabelForTextArea(allergies, setAllergies, "allergies", false)}
                    {floatinglabelForTextArea(lifeStyle, setLifeStyle, "lifeStyle", false)}
                </div>

                <div className="flex flex-column gap-4 w-full visitNotes" style={{ backgroundColor: "rgba(234, 235, 240, 0.3)", padding: "1rem", borderRadius: "15px" }}>
                    <p className="m-0 font-bold" style={{ color: "#2D3967", whiteSpace: "nowrap" }}>{t('vitalSigns')}</p>
                    {floatinglabelForNumber(diastolicBloodPressure, setDiastolicBloodPressure, "diastolicBloodPressure", 0, 40, 120)}
                    {floatinglabelForNumber(systolicBloodPressure, setSystolicBloodPressure, "systolicBloodPressure", 0, 80, 200)}
                    {floatinglabelForNumber(pulse, setPulse, "pulse", 0, 30, 200)}
                    {floatinglabelForNumber(saturationSpO2, setSaturationSpO2, "saturationSpO2", 0, 50, 100)}
                    {floatinglabelForNumber(respiratoryRate, setRespiratoryRate, "respiratoryRate", 0, 5, 40)}
                    {floatinglabelForNumber(bodyTemperature, setBodyTemperature, "bodyTemperature", 2, 30, 45)}
                </div>
            </div>

            <br />

            <div className="flex flex-column gap-4 w-full" style={{ backgroundColor: "rgba(234, 235, 240, 0.3)", padding: "1rem", borderRadius: "15px" }}>
                <p className="m-0 font-bold" style={{ color: "#2D3967", whiteSpace: "nowrap" }}>{t('physicalExamination')}</p>
                <div className="flex gap-4 w-full">
                    <div className="w-full">
                        {floatinglabelForText(jointRangeOfMotion, setJointRangeOfMotion, "jointRangeOfMotion")}
                    </div>
                    <div className="w-full">
                        {floatinglabelForText(muscleStrength, setMuscleStrength, "muscleStrength")}
                    </div>

                </div>
                <div className="flex gap-4 w-full">
                    <div className="w-full">
                        {floatinglabelForText(postureAnalysis, setPostureAnalysis, "postureAnalysis")}
                    </div>
                    <div className="w-full">
                        {floatinglabelForText(painAssessment, setPainAssessment, "painAssessment")}
                    </div>
                </div>
            </div>

            <br />
            <div className="flex flex-column gap-4 w-full">
                <p className="m-0 font-bold" style={{ color: "#2D3967", whiteSpace: "nowrap" }}>{t('diagnosisList')}</p>
                <div className="flex justify-content-between align-items-start gap-4 w-full">

                    <div className="flex gap-1">
                        <i className="pi pi-asterisk p-overlay-badge" style={{ fontSize: '0.5rem', color: 'red' }}></i>
                        <Dropdown
                            value={diagnosisCode}
                            disabled={selectedDate && !today}
                            filter
                            placeholder={t('selectDiagnosisCode')}
                            onChange={(e) => setDiagnosisCode(e.value)}
                            invalid={diagnosisCodeIsNotGiven && diagnosisList.length === 0}
                            options={diagnosisOptions}
                            optionLabel="name"
                            style={{
                                height: '3rem',
                                width: '15rem',
                                borderRadius: "15px",
                            }}
                        />
                    </div>

                    <div className="card flex flex-wrap gap-2 justify-content-end align-items-start w-full">
                        {
                            diagnosisList.map((diagnosis: string) => (
                                <Chip
                                    key={diagnosis}
                                    label={diagnosis}
                                    removable={!(selectedDate && !today)}
                                    onRemove={() => {
                                        setDiagnosisList((prevList: any) =>
                                            prevList.filter((item: any) => item !== diagnosis)
                                        );
                                        return true;
                                    }}
                                />))
                        }
                    </div>
                </div>
            </div>

            <br />

            <div className="flex justify-content-end align-items-start mb-4">
                <Button
                    label={today ? t('update') : t('save')}
                    onClick={addConsultantNote}
                    icon={<img src="/saveVisitIcon.svg" />}
                    disabled={selectedDate && !today}
                    style={{
                        color: "#FFFFFF",
                        backgroundColor: "#6383F4",
                        height: '2.5rem',
                        width: '10rem',
                        borderRadius: "15px",
                        border: "none",
                        position: "fixed",
                        bottom: offset.bottom,
                        right: offset.right,
                        zIndex: 1000
                    }}
                />
            </div>
        </Card>
    )
}


export default VisitInformation;